// ── Table primitive — mirrors every grid shown in 2DD ─────────────────────────
export interface Table {
  headers: string[];
  rows: Record<string, any>[];
}

function makeTable(headers: string[], rows: Record<string, any>[]): Table {
  return { headers, rows };
}

// ── Shared base ───────────────────────────────────────────────────────────────
export interface BasePage {
  pageType: string;
  pageName: string;
  pageNumber: number;
  images?: string[];
}

// ── Per-page display interfaces ───────────────────────────────────────────────

export interface DimensionPage extends BasePage {
  'CARCASS SPECIFICATIONS': {
    'Skirting': Table;                    // Heading | Detail | Value
    'Carcass Depths': Table;              // Part Type | Depths
    'Loft Slab Present': string | null;
    'Pooja Mandir': string | null;
    'Dimension T-Patti Data': any | null;
  };
  'Modules on Drawing': string[];
}

export interface MaterialPage extends BasePage {
  'SHUTTER SPECIFICATIONS': {
    'Color Specification': Table;         // Name | Laminate Color | Code
    'Handle': Table;                      // Has Handle
    'Shutter Materials': Table;           // Shutter Material | Shutter Finish (or Frame Name | Frame Color)
  };
}

export interface InternalPage extends BasePage {
  'Accessories': Table;                   // ID | Name | Category
}

export interface ModuleListPage extends BasePage {
  'Modules': Table;                       // Module Code | Module Description | Dimension | Accessory Details | Handle Details
}

export interface AppliancesPage extends BasePage {
  'Appliances': Table;                    // Name | Supplied By | Installed By | Description | Cutting Dimension | Quantity
}

export interface FlooringPage extends BasePage {
  'FLOORING SPECIFICATIONS': {
    'FLOOR DETAILS': Table;
    'FLOOR ACCESSORY DETAILS': Table;
    'FLOOR INSTALLATION': Table;
  };
}

export interface DemolitionPage extends BasePage {
  'Demolition Details': Table;            // Name | Qty | UOM
}

export interface CivilWorkPage extends BasePage {
  'Civil Work Details': Table;            // Name | Qty | UOM
}

export interface OtherServicesPage extends BasePage {
  'Services': Table;                      // Service Name | HomeLane Scope
}

export interface RFQPage extends BasePage {
  'RFQ Details': Table;                   // Category Name | Sub Category Name | SKU Name | Material Details | Dimension | Quantity | UOM
}

export interface DQCPage extends BasePage {
  'DQC Comments': Table;                  // Category | Design Error | Exception Reasoning
}

export interface FalseCeilingPage extends BasePage {
  'False Ceiling Details': Table;         // False Ceiling Required | Area for Ceiling | False Ceiling Groups
  'Light Fixtures': Table;               // Name | Type | No of Lights | Colour Code Wattage | Running Length (ft)
}

export interface CurtainsPage extends BasePage {
  'Curtain Details': Table;               // Fabric | Hardware | Panel Details
}

export interface ScopeOfWorkPage extends BasePage {
  'Scope of Work': Table;                 // Description | CX - SOW | HomeLane SOW | Additional Details
}

export interface WallElectricalPage extends BasePage {
  'Annotation to Service Mapping': Table; // Annotation | Service
  'Electrical Points': Table;             // Caption | Description | Point Type
}

export interface PlanPage extends BasePage {
  'CARCASS SPECIFICATIONS': {
    'Carcass Materials': Table;  // Section | Material | Color | Ref Code
    'Handles': Table;            // Item | Size | Type | Budget
    'Profile': Table;            // Name | Type
    'Frame & Shutter': Table;    // Specification | Value
  };
}

export interface OverviewPage extends BasePage {}

// ── Abstract extractor ────────────────────────────────────────────────────────

abstract class PageExtractor<T extends BasePage> {
  abstract readonly types: string[];

  protected base(page: any): BasePage {
    return {
      pageType: page.type,
      pageName: page.name,
      pageNumber: page.pageNumber,
    };
  }

  protected bool(val: any): string | null {
    if (val === null || val === undefined) return null;
    if (typeof val === 'object') return Object.keys(val).length > 0 ? 'YES' : null;
    return String(val).toUpperCase();
  }

  protected imageUrls(...vals: any[]): string[] {
    const urls: string[] = [];
    vals.forEach(v => {
      if (!v) return;
      if (typeof v === 'string') { urls.push(v); return; }
      if (Array.isArray(v)) {
        v.forEach((item: any) => {
          if (typeof item === 'string') urls.push(item);
          else if (item?.imageUrl) urls.push(item.imageUrl);
          else if (item?.imageName) urls.push(item.imageName);
        });
        return;
      }
      if (typeof v === 'object') {
        if (v.imageUrl) urls.push(v.imageUrl);
        else if (v.imageName) urls.push(v.imageName);
      }
    });
    return urls;
  }

  // Convert a key-value map into a two-column Key | Value table
  protected kvTable(obj: Record<string, any> | null): Table {
    if (!obj) return makeTable(['Key', 'Value'], []);
    return makeTable(
      ['Key', 'Value'],
      Object.entries(obj).map(([k, v]) => ({ Key: k, Value: v }))
    );
  }

  abstract extract(page: any): T;
}

// ── Concrete extractors ───────────────────────────────────────────────────────

class DimensionExtractor extends PageExtractor<DimensionPage> {
  readonly types = ['dimension'];

  extract(page: any): DimensionPage {
    const skirtingRows: Record<string, any>[] = [];
    (page.skirting || []).forEach((s: any) => {
      Object.entries(s.details || {}).forEach(([k, v]) => {
        skirtingRows.push({ Heading: s.heading || '', Detail: k, Value: v });
      });
    });

    return {
      ...this.base(page),
      images: this.imageUrls(page.elevationImage),
      'CARCASS SPECIFICATIONS': {
        'Skirting': makeTable(['Heading', 'Detail', 'Value'], skirtingRows),
        'Carcass Depths': makeTable(
          ['Part Type', 'Depths'],
          (page.carcassDepthList || []).map((c: any) => ({
            'Part Type': c.partType,
            'Depths': c.depths,
          }))
        ),
        'Loft Slab Present': this.bool(page.loftSlabPresent),
        'Pooja Mandir': this.bool(page.poojaMandir),
        'Dimension T-Patti Data': page.dimensionTPattiData ?? null,
      },
      'Modules on Drawing': (page.metaData?.annotationMetaData?.modules || [])
        .map((m: any) => m.annotation),
    };
  }
}

class MaterialExtractor extends PageExtractor<MaterialPage> {
  readonly types = ['material', 'shuttermaterial', 'shutterextension'];

  extract(page: any): MaterialPage {
    const colorRows: Record<string, any>[] = [];
    const handleRows: Record<string, any>[] = [];
    const materialRows: Record<string, any>[] = [];

    Object.values(page.shutterDetails || {}).forEach((details: any) => {
      if (!Array.isArray(details)) return;
      details.forEach((item: any) => {
        if ('laminateColorName' in item) {
          colorRows.push({
            Name: item.annotationName,
            'Laminate Color': item.laminateColorName,
            Code: item.laminateColorCode,
          });
        } else if ('hasHandle' in item) {
          handleRows.push({ 'Has Handle': item.hasHandle ? 'YES' : 'NO' });
        } else if ('shutterMaterial' in item || 'frameName' in item) {
          materialRows.push(
            item.shutterMaterial
              ? { 'Shutter Material': item.shutterMaterial, 'Shutter Finish': item.shutterFinish }
              : { 'Frame Name': item.frameName, 'Frame Color': item.frameColor }
          );
        }
      });
    });

    return {
      ...this.base(page),
      images: this.imageUrls(page.elevationImage),
      'SHUTTER SPECIFICATIONS': {
        'Color Specification': makeTable(['Name', 'Laminate Color', 'Code'], colorRows),
        'Handle': makeTable(['Has Handle'], handleRows),
        'Shutter Materials': makeTable(
          materialRows.length && 'Frame Name' in materialRows[0]
            ? ['Frame Name', 'Frame Color']
            : ['Shutter Material', 'Shutter Finish'],
          materialRows
        ),
      },
    };
  }
}

class InternalExtractor extends PageExtractor<InternalPage> {
  readonly types = ['internal'];

  extract(page: any): InternalPage {
    return {
      ...this.base(page),
      images: this.imageUrls(page.elevationImage),
      'Accessories': makeTable(
        ['ID', 'Name', 'Category'],
        (page.accessorySpecificationList || []).map((a: any) => {
          const parts = (a.accessoryName || '').split('#');
          return {
            ID: a.accessoryAnnotationId,
            Name: parts[0]?.trim() || '',
            Category: parts[1]?.trim() || '',
          };
        })
      ),
    };
  }
}

class ModuleListExtractor extends PageExtractor<ModuleListPage> {
  readonly types = ['modulelist'];

  extract(page: any): ModuleListPage {
    const rows: Record<string, any>[] = [];
    Object.values(page.moduleList || {}).forEach((subcat: any) => {
      (subcat || []).forEach((row: any) => {
        rows.push({
          'Module Code': row.moduleAnnotation,
          'Module Description': row.name,
          'Dimension': row.moduleDimension,
          'Accessory Details': row.accessoryDetails,
          'Handle Details': row.handleDetails,
        });
      });
    });
    return {
      ...this.base(page),
      'Modules': makeTable(
        ['Module Code', 'Module Description', 'Dimension', 'Accessory Details', 'Handle Details'],
        rows
      ),
    };
  }
}

class AppliancesExtractor extends PageExtractor<AppliancesPage> {
  readonly types = ['appliances'];

  extract(page: any): AppliancesPage {
    return {
      ...this.base(page),
      'Appliances': makeTable(
        ['Name', 'Supplied By', 'Installed By', 'Description', 'Cutting Dimension', 'Quantity', 'Image'],
        (page.applianceList || []).map((a: any) => ({
          'Name': a.name,
          'Supplied By': a.suppliedBy,
          'Installed By': a.installedBy,
          'Description': a.description,
          'Cutting Dimension': a.cuttingDimension,
          'Quantity': a.quantity,
          'Image': a.imageUrl || a.image || null,
        }))
      ),
    };
  }
}

class FlooringExtractor extends PageExtractor<FlooringPage> {
  readonly types = ['flooring', 'woodenflooring'];

  extract(page: any): FlooringPage {
    const fm = page.floorTilesMetaData || {};
    return {
      ...this.base(page),
      images: this.imageUrls(page.elevationImage),
      'FLOORING SPECIFICATIONS': {
        'FLOOR DETAILS': this.kvTable(fm.floorDetailsList ?? null),
        'FLOOR ACCESSORY DETAILS': this.kvTable(fm.floorAccessoryDetails ?? null),
        'FLOOR INSTALLATION': this.kvTable(fm.floorInstallationDetails ?? null),
      },
    };
  }
}

class DemolitionExtractor extends PageExtractor<DemolitionPage> {
  readonly types = ['demolitionroom', 'demolition'];

  extract(page: any): DemolitionPage {
    return {
      ...this.base(page),
      'Demolition Details': makeTable(
        ['Name', 'Qty', 'UOM'],
        (page.demolitionDetails || []).map((d: any) => ({
          'Name': d.name, 'Qty': d.qty, 'UOM': d.uom,
        }))
      ),
    };
  }
}

class CivilWorkExtractor extends PageExtractor<CivilWorkPage> {
  readonly types = ['civilwork', 'civilfloormarblelaying', 'civilfloortilelay', 'civilwalltilelay'];

  extract(page: any): CivilWorkPage {
    return {
      ...this.base(page),
      'Civil Work Details': makeTable(
        ['Name', 'Qty', 'UOM'],
        (page.civilWorkDetails || []).map((d: any) => ({
          'Name': d.name, 'Qty': d.qty, 'UOM': d.uom,
        }))
      ),
    };
  }
}

class OtherServicesExtractor extends PageExtractor<OtherServicesPage> {
  readonly types = ['otherservices'];

  extract(page: any): OtherServicesPage {
    return {
      ...this.base(page),
      'Services': makeTable(
        ['Service Name', 'HomeLane Scope'],
        (page.otherServicesList || page.servicesList || []).map((s: any) => ({
          'Service Name': s.service_name,
          'HomeLane Scope': s.homelane_scope,
        }))
      ),
    };
  }
}

class RFQExtractor extends PageExtractor<RFQPage> {
  readonly types = ['rfq'];

  extract(page: any): RFQPage {
    const r = page.rfqDetails || {};
    return {
      ...this.base(page),
      'RFQ Details': makeTable(
        ['Category Name', 'Sub Category Name', 'SKU Name', 'Material Details', 'Dimension', 'Quantity', 'UOM'],
        [{
          'Category Name': r.categoryName,
          'Sub Category Name': r.subCategoryName,
          'SKU Name': r.skuName,
          'Material Details': r.materialDetails,
          'Dimension': r.dimension,
          'Quantity': r.quantity,
          'UOM': r.uom,
        }]
      ),
    };
  }
}

class DQCExtractor extends PageExtractor<DQCPage> {
  readonly types = ['dqc'];

  extract(page: any): DQCPage {
    return {
      ...this.base(page),
      'DQC Comments': makeTable(
        ['Category', 'Design Error', 'Exception Reasoning'],
        (page.dqcComments || []).map((d: any) => ({
          'Category': d.category,
          'Design Error': d.designErrorText,
          'Exception Reasoning': d.exceptionReasoning,
        }))
      ),
    };
  }
}

class FalseCeilingExtractor extends PageExtractor<FalseCeilingPage> {
  readonly types = [
    'falseceiling', 'falseceilingoverview', 'falseceilingreflectedview',
    'falseceilingsectionview', 'falseceilingplanview', 'falseceilingpaintview',
    'falseceilinglightingview', 'falseceilingelectricalview',
    'falseceilingelectricalloopingview', 'customfalseceilingoverview',
    'customfalseceilingsection', 'customfalseceilingelectricalsow',
    'customfalseceilingplan', 'customfalseceilinglightlayout',
    'customfalseceilingloopinglayout',
  ];

  extract(page: any): FalseCeilingPage {
    const fc = page.metaData?.falseCeilingDetails;
    return {
      ...this.base(page),
      'False Ceiling Details': makeTable(
        ['False Ceiling Required', 'Area for Ceiling', 'False Ceiling Groups'],
        fc ? [{
          'False Ceiling Required': fc.falseCeilingRequired,
          'Area for Ceiling': fc.areaForCeiling,
          'False Ceiling Groups': fc.falseCeilingGroups,
        }] : []
      ),
      'Light Fixtures': makeTable(
        ['Name', 'Type', 'No of Lights', 'Colour Code Wattage', 'Running Length (ft)'],
        (page.metaData?.lightFixtures || []).map((l: any) => ({
          'Name': l.name,
          'Type': l.type,
          'No of Lights': l.noOfLights,
          'Colour Code Wattage': l.colourCodeWattage,
          'Running Length (ft)': l.runningLengthInFeet,
        }))
      ),
    };
  }
}

class CurtainsExtractor extends PageExtractor<CurtainsPage> {
  readonly types = ['curtains', 'curtainsyellow', 'curtainsdecor', 'curtainsaumbre'];

  extract(page: any): CurtainsPage {
    return {
      ...this.base(page),
      images: this.imageUrls(page.images),
      'Curtain Details': makeTable(
        ['Fabric', 'Hardware', 'Panel Details'],
        (page.curtainsDetails || []).map((c: any) => ({
          'Fabric': c.fabric,
          'Hardware': c.hardware,
          'Panel Details': c.panelDetails,
        }))
      ),
    };
  }
}

class ScopeOfWorkExtractor extends PageExtractor<ScopeOfWorkPage> {
  readonly types = ['customscopeofworktable', 'scopeofwork'];

  private readonly SOW_ITEMS = [
    'False Ceiling', 'Floor Covering', 'Paint for False ceiling',
    'Light Supply', 'Light Installation', 'Additional Support for Lights',
    'Wiring / Looping', 'Create / Additional of Points',
    'Switch & Sockets Supply', 'Fan supply', 'Extended Fan Rod Supply',
    'Fan & Fan Rod Installation',
  ];

  extract(page: any): ScopeOfWorkPage {
    const meta = page.metaData || {};
    return {
      ...this.base(page),
      'Scope of Work': makeTable(
        ['Description', 'CX - SOW', 'HomeLane SOW', 'Additional Details'],
        this.SOW_ITEMS.map(item => ({
          'Description': item,
          'CX - SOW': meta[item]?.['CX'] ?? null,
          'HomeLane SOW': meta[item]?.['HL'] ?? null,
          'Additional Details': meta[item]?.['additionalDetails'] ?? null,
        }))
      ),
    };
  }
}

class WallElectricalExtractor extends PageExtractor<WallElectricalPage> {
  readonly types = ['wallelectrical'];

  extract(page: any): WallElectricalPage {
    return {
      ...this.base(page),
      'Annotation to Service Mapping': makeTable(
        ['Annotation', 'Service'],
        (page.serviceAnnotationMapping || []).map((s: any) => ({
          Annotation: s.annotation,
          Service: s.service,
        }))
      ),
      'Electrical Points': makeTable(
        ['Caption', 'Description', 'Point Type'],
        (page.electricalData || []).map((e: any) => ({
          Caption: e.caption,
          Description: e.description,
          'Point Type': e.pointType,
        }))
      ),
    };
  }
}

class PlanExtractor extends PageExtractor<PlanPage> {
  readonly types = ['plan'];

  extract(page: any): PlanPage {
    const carcaseDetails: Record<string, any> = page.carcaseDetails || {};
    const carcassMaterialRows: Record<string, any>[] = [];
    const handleRows: Record<string, any>[] = [];
    const profileRows: Record<string, any>[] = [];
    const frameShutterRows: Record<string, any>[] = [];

    Object.entries(carcaseDetails).forEach(([key, details]: [string, any]) => {
      if (!details) return;

      if (key.includes('HANDLES') && Array.isArray(details) && details.length) {
        details.forEach((h: any) => handleRows.push({
          'Item': h.item,
          'Size': h.size,
          'Type': h.partType,
          'Budget': h.budgetType || 'NA',
        }));
      } else if (key.includes('PROFILE') && Array.isArray(details) && details.length) {
        details.forEach((p: any) => profileRows.push({
          'Name': p.name,
          'Type': p.type,
        }));
      } else if (key === 'frameAndShutterSpecification' && details && !Array.isArray(details)) {
        Object.entries(details).forEach(([specKey, specVal]: [string, any]) => {
          if (specKey === 'heading') return;
          if (specVal && typeof specVal === 'object') {
            frameShutterRows.push({
              'Specification': String(specVal.name || specKey).toUpperCase(),
              'Value': specVal.value != null ? String(specVal.value).toUpperCase() : 'NA',
            });
          } else {
            frameShutterRows.push({
              'Specification': specKey.toUpperCase(),
              'Value': specVal != null ? String(specVal).toUpperCase() : 'NA',
            });
          }
        });
      } else if (Array.isArray(details) && details.length) {
        // Carcass material entries keyed like "BASE_UNIT_0", "WALL_UNIT_1"
        const sectionName = key.split('_').slice(0, -1).join(' ') || key;
        details.forEach((m: any) => {
          if ('carcassMaterial' in m) {
            carcassMaterialRows.push({
              'Section': sectionName,
              'Material': m.carcassMaterial,
              'Color': m.carcassColor,
              'Ref Code': m.carcassColorCode,
            });
          }
        });
      }
    });

    return {
      ...this.base(page),
      images: this.imageUrls(page.measuredPlanImageUrl),
      'CARCASS SPECIFICATIONS': {
        'Carcass Materials': makeTable(['Section', 'Material', 'Color', 'Ref Code'], carcassMaterialRows),
        'Handles': makeTable(['Item', 'Size', 'Type', 'Budget'], handleRows),
        'Profile': makeTable(['Name', 'Type'], profileRows),
        'Frame & Shutter': makeTable(['Specification', 'Value'], frameShutterRows),
      },
    };
  }
}

class OverviewExtractor extends PageExtractor<OverviewPage> {
  readonly types = ['overview', 'overviewsubpage'];

  extract(page: any): OverviewPage {
    return {
      ...this.base(page),
      images: this.imageUrls(
        page.planImageUrl,
        page.roomFloorPlanImageUrl,
        page.perspectiveImages,
      ),
    };
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalizeType(type: string = ''): string {
  return type.toLowerCase().replace(/[0-9]/g, '').replace(/[-_]/g, '');
}

// ── Registry / orchestrator ───────────────────────────────────────────────────

export class DisplayPayloadExtractor {
  private registry = new Map<string, PageExtractor<any>>();

  constructor() {
    const extractors: PageExtractor<any>[] = [
      new DimensionExtractor(),
      new MaterialExtractor(),
      new InternalExtractor(),
      new ModuleListExtractor(),
      new AppliancesExtractor(),
      new FlooringExtractor(),
      new DemolitionExtractor(),
      new CivilWorkExtractor(),
      new OtherServicesExtractor(),
      new RFQExtractor(),
      new DQCExtractor(),
      new FalseCeilingExtractor(),
      new CurtainsExtractor(),
      new ScopeOfWorkExtractor(),
      new WallElectricalExtractor(),
      new PlanExtractor(),
      new OverviewExtractor(),
    ];

    extractors.forEach(e =>
      e.types.forEach(t => this.registry.set(t, e))
    );
  }

  extractPage(page: any): BasePage {
    const type = normalizeType(page.type);
    const extractor = this.registry.get(type);
    if (extractor) return extractor.extract(page);
    return { pageType: page.type, pageName: page.name, pageNumber: page.pageNumber };
  }

  buildPayload(pages: any[]): BasePage[] {
    return (pages || []).map(page => this.extractPage(page));
  }
}
